import React, { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * SoftSignupPrompt — a tiny, dismissible bottom-right toast that gently
 * invites engaged visitors to sign up. Designed to feel like a friendly
 * tap on the shoulder, NOT a popup.
 *
 * Rules of engagement:
 *   1. Only shown to non-logged-in users
 *   2. Only after they've been on-site for `DELAY_MS` (default 60s)
 *   3. Never shown on auth-related pages (login/register/quiz auth gate)
 *   4. Dismissal is sticky for the session (sessionStorage)
 *   5. Re-suppressed if user navigates to /login or /register (we let
 *      them follow through their own intent)
 *   6. Mobile-aware: smaller on phones, doesn't block scroll
 */
const DELAY_MS = 60 * 1000;
const SUPPRESS_KEY = 'eager_softprompt_dismissed';
// Routes where the prompt would be redundant or annoying
const SUPPRESSED_ROUTES = ['/login', '/register', '/auth/callback', '/admin'];

export default function SoftSignupPrompt() {
    const { userData, isLoading } = useUser();
    const location = useLocation();
    const navigate = useNavigate();
    const [visible, setVisible] = useState(false);

    // Reset 'visible' if user signs in
    useEffect(() => {
        if (userData && visible) setVisible(false);
    }, [userData, visible]);

    // Schedule the appearance
    useEffect(() => {
        if (isLoading || userData) return;
        // Suppress if previously dismissed this session
        try {
            if (sessionStorage.getItem(SUPPRESS_KEY) === '1') return;
        } catch { /* private mode — ignore */ }
        // Suppress on routes that are themselves about auth
        if (SUPPRESSED_ROUTES.some(r => location.pathname.startsWith(r))) return;

        const t = setTimeout(() => setVisible(true), DELAY_MS);
        return () => clearTimeout(t);
    }, [isLoading, userData, location.pathname]);

    const dismiss = (suppress = true) => {
        setVisible(false);
        if (suppress) {
            try { sessionStorage.setItem(SUPPRESS_KEY, '1'); } catch { /* noop */ }
        }
    };

    if (!visible) return null;

    return (
        <div
            data-testid="soft-signup-prompt"
            className="fixed z-50 bottom-4 right-4 sm:bottom-6 sm:right-6 max-w-[20rem] sm:max-w-sm animate-fade-slide"
        >
            <div className="relative bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-4 sm:p-5">
                {/* Close button */}
                <button
                    onClick={() => dismiss(true)}
                    data-testid="soft-signup-prompt-close"
                    aria-label="Dismiss"
                    className="absolute top-2.5 right-2.5 w-7 h-7 inline-flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-800 hover:text-white transition-colors"
                >
                    <X className="w-3.5 h-3.5" />
                </button>

                <div className="flex items-start gap-3 pr-6">
                    <div className="w-9 h-9 flex-shrink-0 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 inline-flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white leading-snug mb-1">
                            Like what you're exploring?
                        </p>
                        <p className="text-xs text-slate-400 leading-relaxed mb-3">
                            Save colleges, get cutoff alerts, and unlock your career match — free.
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => { dismiss(false); navigate('/register'); }}
                                data-testid="soft-signup-prompt-cta"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition-colors"
                            >
                                Join free <ArrowRight className="w-3 h-3" />
                            </button>
                            <button
                                onClick={() => dismiss(true)}
                                data-testid="soft-signup-prompt-later"
                                className="text-xs text-slate-500 hover:text-slate-300 px-2 py-1.5 transition-colors"
                            >
                                Maybe later
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
